import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ConfirmModal from './shared/ConfirmModal';
import marketplaceOverrideService from '../services/marketplaceOverrideService';
import './PaymentPorterPage.css';   // pp-rules-add-btn — the portal-wide "+ Add X" button
import './MarketplaceOverrides.css';

// Marketplace Overrides for one entity — this partner/chain/hotel's own value for a marketplace setting,
// replacing the platform default.
//
// Entity-agnostic on purpose. It takes the same { entityType, entityId } pair as CredentialsTab, and the
// route it sends Add and Edit to is passed in, so the partner and hotel screens mount this component
// rather than growing a copy of it.
const MarketplaceOverridesTab = ({ entityType, entityId, basePath }) => {
    const navigate = useNavigate();
    const [overrides, setOverrides] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [confirmModal, setConfirmModal] = useState(null);

    const load = useCallback(async () => {
        if (!entityId) return;
        setLoading(true);
        setError(null);
        try {
            const res = await marketplaceOverrideService.list(entityType, entityId);
            setOverrides(res.data?.overrides || []);
        } catch (err) {
            setError(err.response?.data?.error?.message || 'Failed to load marketplace overrides');
        } finally {
            setLoading(false);
        }
    }, [entityType, entityId]);

    useEffect(() => { load(); }, [load]);

    const handleDelete = (row) => {
        setConfirmModal({
            title: 'Delete Override',
            message: `Remove the override for "${row.setting_name}" on ${row.marketplace_name}? `
                + 'The marketplace default will apply again.',
            onConfirm: async () => {
                try {
                    await marketplaceOverrideService.remove(row.id);
                    await load();
                } catch (err) {
                    setError(err.response?.data?.error?.message || 'Failed to delete override');
                }
            },
        });
    };

    return (
        <div className="mko-tab">
            <div className="mko-header">
                <div className="mko-intro">
                    Values set here replace the marketplace default for this {entityType}. A setting with no
                    override keeps the default.
                </div>
                <button
                    type="button"
                    className="pp-rules-add-btn"
                    onClick={() => navigate(`${basePath}/marketplace-overrides/new`)}
                >
                    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                        <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    Add Override
                </button>
            </div>

            {error && <div className="mko-error">{error}</div>}

            {loading ? (
                <div className="mko-loading">Loading…</div>
            ) : overrides.length === 0 ? (
                <div className="mko-empty">No marketplace overrides. Add one to replace a marketplace default.</div>
            ) : (
                <div className="mko-table-wrap">
                    <table className="mko-table">
                        <thead>
                            <tr>
                                <th>Marketplace</th>
                                <th>Setting</th>
                                <th>Value</th>
                                <th>Default</th>
                                <th className="mko-actions-col">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {overrides.map(row => (
                                <tr key={row.id}>
                                    <td>
                                        <span className="mko-marketplace">
                                            {row.marketplace_image && (
                                                <img src={row.marketplace_image} alt="" className="mko-marketplace-img" />
                                            )}
                                            {row.marketplace_name}
                                        </span>
                                    </td>
                                    <td>
                                        <span className="mko-setting-name">{row.setting_name}</span>
                                        {row.setting_label && <span className="mko-setting-label">{row.setting_label}</span>}
                                    </td>
                                    <td className="mko-value">{row.setting_value || <span className="mko-muted">(empty)</span>}</td>
                                    <td className="mko-value mko-muted">{row.default_value || '—'}</td>
                                    <td className="mko-actions-col">
                                        <div className="mko-row-actions">
                                            <button
                                                type="button"
                                                className="mko-btn"
                                                onClick={() => navigate(`${basePath}/marketplace-overrides/${row.id}`)}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                type="button"
                                                className="mko-btn mko-btn-danger"
                                                onClick={() => handleDelete(row)}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {confirmModal && (
                <ConfirmModal
                    title={confirmModal.title}
                    message={confirmModal.message}
                    confirmLabel="Delete"
                    danger
                    onConfirm={() => { confirmModal.onConfirm(); setConfirmModal(null); }}
                    onCancel={() => setConfirmModal(null)}
                />
            )}
        </div>
    );
};

export default MarketplaceOverridesTab;
